import { z } from "zod";
import { containerObjectSchema } from "./container.js";
/** Link an existing container under this one (POST /containers/:id/children). */
export const linkChildSchema = z.object({
    childId: z.string().uuid("childId must be a UUID"),
});
/**
 * Move a container under a new parent; null parentId moves it to root.
 * An optional `entryId` reassigns the container as a root of that catalog
 * entry (its subtree following; exclusive membership removes it from every
 * other entry). When the destination entry belongs to another user's home the
 * move becomes an ownership transfer (ticket 06): the subtree relocates to that
 * home and `confirmTransfer` is required.
 */
export const moveContainerSchema = z.object({
    parentId: z.string().uuid().nullable(),
    entryId: z.string().uuid().optional(),
    version: z.number().int().nonnegative(),
    /**
     * Explicit ownership-transfer confirmation (ticket 06). Required when the
     * destination entry belongs to another user's home: the old owner must
     * confirm the irreversibility-by-policy before the subtree relocates.
     */
    confirmTransfer: z.boolean().optional(),
});
/**
 * Move response (ticket 06). A same-owner move returns the bare container; a
 * cross-user transfer adds `droppedPlacements` so the UI can warn that item
 * placements from the source home were dropped.
 */
export const moveResultSchema = containerObjectSchema.extend({
    droppedPlacements: z.number().int().nonnegative().optional(),
});
/** Remove a container from under its current parent (DELETE /containers/:id/parent). */
export const unlinkContainerSchema = z.object({
    version: z.number().int().nonnegative(),
});
/** Parent of a container by reverse lookup over the flat catalog. */
export function parentOf(containers, id) {
    return containers.find((c) => c.children.includes(id));
}
/** Direct children of a container; null lists root containers. */
export function childrenOf(containers, id) {
    return id === null
        ? containers.filter((c) => !parentOf(containers, c.id))
        : containers.filter((c) => parentOf(containers, c.id)?.id === id);
}
/** Divider between container levels in a path label (scanner-surface 08). */
export const PATH_SEPARATOR = " / ";
/** The nested path from the root to a container, root first (for breadcrumbs). */
export function pathOf(containers, id) {
    const path = [];
    let cursor = containers.find((c) => c.id === id);
    while (cursor) {
        path.unshift(cursor);
        cursor = parentOf(containers, cursor.id);
    }
    return path;
}
/**
 * Display/breadcrumb label of a container: its path names from the root down
 * to and including the container itself, joined with " / ". Shared so the
 * server's stored `searchText` path and the browser's display path (and the
 * `path:` field the shared extractor emits) can never diverge (ADR-0036).
 */
export function pathLabelOf(containers, id) {
    return pathOf(containers, id)
        .map((c) => c.name)
        .join(PATH_SEPARATOR);
}
/**
 * Display/breadcrumb label of a container with its catalog entry as the root
 * (`Garage / Keller / Regal`), matching the detail breadcrumb's entry root.
 * The entry label is passed in because entries and their `name@owner` form are
 * client-side catalog data; a missing label (single visible entry) keeps the
 * plain {@link pathLabelOf} path so search rows, gallery captions and the
 * breadcrumb cannot render divergent labels (ADR-0036, ticket 12).
 *
 * @param containers the union the container's path is derived from.
 * @param id the container whose path to label.
 * @param entryLabel the catalog entry display name, or null when none applies.
 */
export function entryPathLabelOf(containers, id, entryLabel) {
    const path = pathLabelOf(containers, id);
    return entryLabel ? `${entryLabel}${PATH_SEPARATOR}${path}` : path;
}
/** Every container below a node (used to exclude cycle-forming picker targets). */
export function descendantsOf(containers, id) {
    const out = [];
    const walk = (nodeId) => {
        for (const child of childrenOf(containers, nodeId)) {
            out.push(child);
            walk(child.id);
        }
    };
    walk(id);
    return out;
}
//# sourceMappingURL=tree.js.map